import styled from 'styled-components';

import { ANIM, COLORS, MOBILE, STATUS_COLORS } from '../../theme/tokens';
import { Text } from '../Text/Text';

/** Positioning context for the absolutely-placed panel. */
export const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  /* Square tap target: 32px reads as a row affordance next to a status chip on
     desktop, widened to the app's 48px control size on mobile for the thumb. */
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${COLORS.icon};
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ease,
    color ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
    background: transparent;
  }

  ${MOBILE} {
    width: 48px;
    height: 48px;
  }
`;

/**
 * The dropped panel. Absolutely positioned rather than portalled: it stays inside
 * the trigger's stacking + scroll context, which is what keeps it glued to the
 * kebab when the page scrolls under it. `$side` is measured by ActionMenu before
 * paint: below the trigger, or above it when below would leave the screen.
 */
export const PanelRoot = styled.div<{ $align: 'start' | 'end'; $side: 'below' | 'above' }>`
  position: absolute;
  ${(p) => (p.$side === 'above' ? 'bottom: calc(100% + 6px);' : 'top: calc(100% + 6px);')}
  ${(p) => (p.$align === 'end' ? 'right: 0;' : 'left: 0;')}
  z-index: 20;
  min-width: 200px;
  padding: 6px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 14px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  /* A real shadow, not the page colour: as pageBg this went pale on the light theme and read as a
     whitish smear under the panel. */
  box-shadow: ${COLORS.menuShadow};
`;

export const Item = styled.button<{ $destructive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  /* 40px keeps two items a comfortable thumb apart without making a 3-item menu
     taller than the panel it drops out of. */
  min-height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${(p) => (p.$destructive ? STATUS_COLORS.negative : COLORS.text)};
  text-align: left;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  ${MOBILE} {
    min-height: 48px;
  }
`;

export const ItemLabel = styled(Text)`
  font-size: 14px;
  color: inherit;
`;
